import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { safeToLocaleString } from '@utils/date.ts';

type Props = {
  dateUpload?: string | null;
  dateSessionStart?: string | null;
  dateFinished?: string | null;
};

/**
 * Compact info icon shown in the editor About header. Hover reveals the
 * session upload / start / finish timestamps. Replaces the always-visible
 * bottom-left footer pill (#2323) that overlaid the last caption segment
 * and blocked editing it.
 */
export function SessionDatesTooltip({
  dateUpload,
  dateSessionStart,
  dateFinished
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  if (!dateUpload && !dateSessionStart && !dateFinished) return null;

  const row = (label: string, value: string) => (
    <div className="flex items-baseline gap-2">
      <span className="uppercase tracking-wider text-[10px] opacity-70">
        {label}:
      </span>
      <span className="tabular-nums">{value}</span>
    </div>
  );

  const label = (
    <div className="flex flex-col gap-1 text-xs text-left">
      {dateUpload && row(t('uploaded'), safeToLocaleString(dateUpload))}
      {dateSessionStart &&
        row(t('session-start'), safeToLocaleString(dateSessionStart))}
      {dateFinished &&
        row(t('date-finished'), safeToLocaleString(dateFinished))}
    </div>
  );

  return (
    <Tooltip label={label}>
      <button
        type="button"
        aria-label={t('session-dates') || 'Session dates'}
        className="inline-flex items-center justify-center h-5 w-5 rounded hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <Info size={12} style={{ color: getColour('icon') }} aria-hidden />
      </button>
    </Tooltip>
  );
}
